import React from 'react'
import moment from 'moment'
import Calendar from '@/components/calendar/Calendar'
import {
  IExistingScaleInterval,
  getTimeslotColor,
  getTimeslotTypeColor,
} from '@/components/calendar/calendarTypes'
import { IUpdateDay, IFullSchema } from '@/store/apis/schemas'
import { ETimeslotType } from '@/store/apis/timeslots'
import {
  useLazyAddTimeslotsQuery,
  useLazyUpdateTimeslotQuery,
  useLazyDeleteTimeslotsQuery,
  ETimeslotIdTypes,
} from '@/store/apis/timeslots'

interface IScenesSchemaProps {
  isLoading: boolean
  firstDay: moment.Moment
  schemaData: IFullSchema
  cellType: ETimeslotType
  dayDatas: IUpdateDay[]
  onGetData(): void
  setFirstDay(newFirstDay: moment.Moment): void
  setDayDatas(newDayData: IUpdateDay[]): void
}

const BaseSchema: React.FC<IScenesSchemaProps> = ({
  isLoading,
  firstDay,
  schemaData,
  cellType,
  dayDatas,
  onGetData,
  setFirstDay,
  setDayDatas,
}) => {
  const [addTimeslotsQuery] = useLazyAddTimeslotsQuery()
  const [updatTimeslotQuery] = useLazyUpdateTimeslotQuery()
  const [deleteTimeslotsQuery] = useLazyDeleteTimeslotsQuery()
  const [timeslots, setTimeslots] = React.useState<IExistingScaleInterval[]>([])

  React.useEffect(() => {
    if (schemaData && !firstDay) {
      const nowDate = new Date()
      const schemaDate = new Date(schemaData.schema.schema.startDate)
      const date = nowDate < schemaDate ? schemaDate : nowDate
      setFirstDay(moment(date).startOf('week'))
    }
  }, [schemaData, firstDay])

  React.useEffect(() => {
    if (schemaData) {
      const existingTimeslots = schemaData.schema.timeslots.map(
        (timeslot): IExistingScaleInterval => ({
          id: timeslot.id,
          start: moment(timeslot.startTimestamp),
          end: moment(timeslot.endTimestamp),
          color: getTimeslotColor(timeslot),
          comment: timeslot.comment,
        }),
      )
      setTimeslots(existingTimeslots)
    }
  }, [schemaData])

  const handleAddTimeslot = async (
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
  ): Promise<void> => {
    await addTimeslotsQuery({
      parentType: ETimeslotIdTypes.Schema,
      schemaId: schemaData.schema.schema.id,
      parentId: schemaData.schema.schema.id,
      timeslots: [
        {
          startTimestamp: startTimestamp.toISOString(),
          endTimestamp: endTimestamp.toISOString(),
          type: cellType,
          comment,
        },
      ],
    })
    onGetData()
  }

  const handleUpdateTimeslot = async (
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
  ): Promise<void> => {
    if (schemaData) {
      const existingTimeslot = schemaData.schema.timeslots.find(
        (timeslot) => timeslot.id === id,
      )
      await updatTimeslotQuery({
        id,
        parentType: ETimeslotIdTypes.Schema,
        parentId: schemaData.schema.schema.id,
        timeslot: {
          type: existingTimeslot.type,
          startTimestamp: startTimestamp.toISOString(),
          endTimestamp: endTimestamp.toISOString(),
          comment,
        },
      })
      onGetData()
    }
  }

  const handleDeleteTimeslots = async (ids: number[]): Promise<void> => {
    await deleteTimeslotsQuery({
      schemaId: schemaData.schema.schema.id,
      ids,
    })
    onGetData()
  }

  const handleChangeDayComment = (date: Date, comment: string): void => {
    const foundIndex = dayDatas.findIndex((dayData) =>
      moment(dayData.date).isSame(date, 'day'),
    )
    if (foundIndex !== -1) {
      const newData = [...dayDatas]
      newData[foundIndex].comment = comment
      setDayDatas(newData)
    } else {
      setDayDatas(
        dayDatas.concat([
          {
            date: moment(date).format('yyyy-MM-DD'),
            comment,
          },
        ]),
      )
    }
  }

  return (
    <Calendar
      isLoading={isLoading}
      firstDay={firstDay ?? moment()}
      onFirstDayUpdate={(newFirstDay) => setFirstDay(newFirstDay)}
      minDay={
        schemaData ? moment(schemaData.schema.schema.startDate) : undefined
      }
      maxDay={schemaData ? moment(schemaData.schema.schema.endDate) : undefined}
      createColor={getTimeslotTypeColor(cellType)}
      selectedIntervals={timeslots}
      allowOverlap
      underlyingCellConfigs={[]}
      onIntervalSelect={handleAddTimeslot}
      onIntervalUpdate={handleUpdateTimeslot}
      onIntervalsRemove={handleDeleteTimeslots}
      onChangeDayComment={handleChangeDayComment}
      calendarDayComment={
        schemaData
          ? schemaData.schema.days.map((day) => ({
              date: new Date(day.date),
              comment: day.comment,
            }))
          : []
      }
    />
  )
}

export default BaseSchema
